import { DateTime } from "luxon";
import { TextView } from "../../../components";
import { htmlToText } from "../../../utils/helpers";
import { useNavigate } from "react-router-dom";
import { Stack } from "@mui/material";
import UserImage from "../../UserImage";
import MagicWand from "../../../assets/icons/MagicWand";
import CopiedFromCloneIcon from "../../Icons/CopiedFromCloneIcon";
import { ReactComponent as CloneGreen } from "../../../assets/images/clone_green.svg";
import { ReactComponent as UnlockYellow } from "../../../assets/images/unlock_yellow.svg";
import { ReactComponent as UnlockIcon } from "../../../assets/icons/wheelMenuIcons/unlock.svg";
import IyugaTooltip from "../../IyugaTooltip";

export const WHEEL_TYPE_MAP = {
  1: "Created",
  2: "Cloned",
  3: "Edit Clone",
  4: "Unlocked",
  5: "Locked",
};

const ClickableTitle = ({ row }) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (row?.id) {
      navigate(`/wheel/${row.id}`);
    }
  };

  return (
    <TextView
      onClick={handleClick}
      style={{ cursor: "pointer", color: "#9b5af8" }}
    >
      {htmlToText(row?.wheel_name)}
    </TextView>
  );
};

const getWheelTypeIcon = (wheelType, row) => {
  const renderLockIcon = row?.is_locked && (wheelType === 1 || wheelType === 3);

  switch (wheelType) {
    case 1:
      return (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <IyugaTooltip tooltip="Created" placement="top">
            <MagicWand width={20} height={20} />
          </IyugaTooltip>
          {renderLockIcon && (
            <IyugaTooltip tooltip="Locked" placement="top">
              <UnlockIcon width={20} height={20} />
            </IyugaTooltip>
          )}
        </Stack>
      );
    case 2:
      return (
        <IyugaTooltip tooltip="Cloned" placement="top">
          <CloneGreen width={20} height={20} />
        </IyugaTooltip>
      );
    case 3:
      return (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <IyugaTooltip tooltip="Edited Clone" placement="top">
            <CopiedFromCloneIcon width={20} height={20} />
          </IyugaTooltip>
          {renderLockIcon && (
            <IyugaTooltip tooltip="Locked" placement="top">
              <UnlockIcon width={20} height={20} />
            </IyugaTooltip>
          )}
        </Stack>
      );
    case 4:
      return (
        <IyugaTooltip tooltip="Unlocked" placement="top">
          <UnlockYellow width={20} height={20} />
        </IyugaTooltip>
      );
    case 5:
      return (
        <IyugaTooltip tooltip="Locked" placement="top">
          <UnlockIcon width={20} height={20} />
        </IyugaTooltip>
      );
    default:
      return (
        <IyugaTooltip tooltip="Created" placement="top">
          <MagicWand width={20} height={20} />
        </IyugaTooltip>
      );
  }
};

const ClickableUserName = ({ row }) => {
  const navigate = useNavigate();

  const hasFirstName = Boolean(row?.first_name);
  const hasLastName = Boolean(row?.last_name);
  const hasAnyName = hasFirstName || hasLastName;
  const displayName = hasAnyName
    ? `${row?.first_name || ""} ${row?.last_name || ""}`.trim()
    : "-";

  const handleClick = () => {
    if (hasAnyName && row?.user_id) {
      navigate(`/gallery/${row.user_id}`);
    }
  };

  return (
    <Stack
      direction={"row"}
      gap={1}
      alignItems={"center"}
      onClick={hasAnyName && row?.user_id ? handleClick : undefined}
    >
      <UserImage image={row?.profile_image} size={28} />
      <TextView
        style={{
          cursor: hasAnyName && row?.user_id ? "pointer" : "default",
          color: hasAnyName && row?.user_id ? "#9b5af8" : "#123051",
        }}
      >
        {displayName}
      </TextView>
    </Stack>
  );
};

export const ANALYTICS_TABLE_COLUMNS = [
  {
    name: "Title",
    selector: (row) => <ClickableTitle row={row} />,
    sortable: true,
    sortField: "wheel_name",
  },
  {
    name: "Type",
    selector: (row) => getWheelTypeIcon(row?.wheel_type, row),
    sortable: true,
    sortField: "wheel_type",
  },
  {
    name: "Date Created",
    selector: (row) => (
      <TextView>
        {new Date(row?.created_at).toLocaleDateString("en-US")}
      </TextView>
    ),
    // selector: (row) => (
    //   <Badge colorScheme={"0"}>
    //     {"Standard"}
    //   </Badge>
    // ),
    sortable: true,
    sortField: "created_at",
  },
  {
    name: "Views",
    selector: (row) => {
      return <TextView>{row?.num_views}</TextView>;
    },
    sortable: true,
    sortField: "views",
  },
  {
    name: "Clones",
    selector: (row) => <TextView>{row?.num_clones || 0}</TextView>,
    sortable: true,
    sortField: "clones",
  },
  {
    name: "Favorites",
    selector: (row) => <TextView>{row?.num_favorites}</TextView>,
    sortable: true,
    sortField: "favorites",
  },
  {
    name: "Unlocks",
    selector: (row) => <TextView>{row?.num_unlocks}</TextView>,
    sortable: true,
    sortField: "direct_unlocks",
  },
  // {
  //   name: "Lock",
  //   selector: (row) => <TextView>{row?.is_locked ? "Yes" : "No"}</TextView>,

  //   sortable: true,
  //   sortField: "lock",
  // },
  // {
  //   name: "Direct Unlocks",
  //   selector: (row) => <TextView>{row?.direct_unlocks}</TextView>,
  //   sortable: true,
  //   sortField: "direct_unlocks",
  // },
  // {
  //   name: "Indirect Unlocks",
  //   selector: (row) => <TextView>{row?.indirect_unlocks}</TextView>,
  //   sortable: true,
  //   sortField: "indirect_unlocks",
  // },
];

export const CLONES_COLUMNS = [
  {
    name: "Name",
    selector: (row) => <ClickableUserName row={row} />,
    sortable: true,
  },
  {
    name: "Date",
    selector: (row) => (
      <TextView>{tableDateFormmater(row?.created_at)}</TextView>
    ),
    sortable: true,
  },
];

export const tableDateFormmater = (date) =>
  date
    ? DateTime.fromISO(date).toLocaleString({
        month: "numeric",
        day: "numeric",
        year: "numeric",
      })
    : "N/A";
